/**
 * SessionStorage Service
 * 
 * Provides encapsulated access to sessionStorage with proper error handling,
 * data validation, and consistent key management across the application.
 */

import securityLogger from '../utils/securityLogger';

// SessionStorage key constants
const STORAGE_KEYS = {
  // Authentication
  TOKEN: 'token',
  
  // User Settings
  USER_SETTINGS: 'userSettings',
  
  // ChatGIS Module
  CHATGIS_MESSAGES: 'chatgis_chat_messages',
  CHATGIS_SYSTEM_PROMPT: 'chatgis_system_prompt',
  CHATGIS_GEO_NER_ENABLED: 'chatgis_geo_ner_enabled',
  CHATGIS_MAP_STATE: 'chatgis_map_state',
  CHATGIS_ABOUT_EXPANDED: 'chatgis_about_expanded',
  CHATGIS_MCP_TOOLS: 'chatgis_mcp_tools',
  
  // RAG Module
  RAG_MESSAGES: 'rag_chat_messages',
  RAG_SESSION_ID: 'rag_session_id',
  RAG_SELECTED_MODEL: 'rag_selected_model',
  RAG_GEO_NER_ENABLED: 'rag_geo_ner_enabled',
  RAG_MAP_STATE: 'rag_map_state',
  RAG_ABOUT_EXPANDED: 'rag_about_expanded'
};

/**
 * Generic storage operations with error handling
 */
const storageOperations = {
  /**
   * Get a string value from sessionStorage
   * @param {string} key - Storage key
   * @returns {string|null} - Value or null if not found/error
   */
  getString(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      securityLogger.error(`Failed to get string from sessionStorage for key '${key}'`, { error: error.message });
      return null;
    }
  },

  /**
   * Set a string value in sessionStorage
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @returns {boolean} - Success status
   */
  setString(key, value) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      securityLogger.error(`Failed to set string in sessionStorage for key '${key}'`, { error: error.message });
      return false;
    }
  },

  /**
   * Get a JSON object from sessionStorage
   * @param {string} key - Storage key
   * @returns {object|null} - Parsed object or null if not found/error
   */
  getJSON(key) {
    try {
      const value = sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      securityLogger.error(`Failed to get JSON from sessionStorage for key '${key}'`, { error: error.message });
      return null;
    }
  },

  /**
   * Set a JSON object in sessionStorage
   * @param {string} key - Storage key
   * @param {object} value - Object to store
   * @returns {boolean} - Success status
   */
  setJSON(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      securityLogger.error(`Failed to set JSON in sessionStorage for key '${key}'`, { error: error.message });
      return false;
    }
  },

  /**
   * Remove an item from sessionStorage
   * @param {string} key - Storage key
   * @returns {boolean} - Success status
   */
  remove(key) {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      securityLogger.error(`Failed to remove item from sessionStorage for key '${key}'`, { error: error.message });
      return false;
    }
  }
};

/**
 * Authentication storage methods
 */
export const authStorage = {
  /**
   * Get authentication token
   * @returns {string|null} - Token or null if not found
   */
  getToken() {
    return storageOperations.getString(STORAGE_KEYS.TOKEN);
  },

  /**
   * Set authentication token
   * @param {string} token - JWT token
   * @returns {boolean} - Success status
   */
  setToken(token) {
    return storageOperations.setString(STORAGE_KEYS.TOKEN, token);
  },

  /**
   * Remove authentication token
   * @returns {boolean} - Success status
   */
  removeToken() {
    return storageOperations.remove(STORAGE_KEYS.TOKEN);
  }
};

/**
 * User settings storage methods
 */
export const settingsStorage = {
  /**
   * Get user settings
   * @returns {object|null} - Settings object or null if not found
   */
  getUserSettings() {
    return storageOperations.getJSON(STORAGE_KEYS.USER_SETTINGS);
  },

  /**
   * Set user settings
   * @param {object} settings - Settings object
   * @returns {boolean} - Success status
   */
  setUserSettings(settings) {
    return storageOperations.setJSON(STORAGE_KEYS.USER_SETTINGS, settings);
  },

  /**
   * Remove user settings
   * @returns {boolean} - Success status
   */
  removeUserSettings() {
    return storageOperations.remove(STORAGE_KEYS.USER_SETTINGS);
  }
};

/**
 * ChatGIS module storage methods
 */
export const chatGISStorage = {
  /**
   * Get chat messages
   * @returns {array|null} - Messages array or null if not found
   */
  getMessages() {
    return storageOperations.getJSON(STORAGE_KEYS.CHATGIS_MESSAGES);
  },

  /**
   * Set chat messages
   * @param {array} messages - Messages array
   * @returns {boolean} - Success status
   */
  setMessages(messages) {
    return storageOperations.setJSON(STORAGE_KEYS.CHATGIS_MESSAGES, messages);
  },

  /**
   * Remove chat messages
   * @returns {boolean} - Success status
   */
  removeMessages() {
    return storageOperations.remove(STORAGE_KEYS.CHATGIS_MESSAGES);
  },

  /**
   * Get system prompt
   * @returns {string|null} - System prompt or null if not found
   */
  getSystemPrompt() {
    return storageOperations.getString(STORAGE_KEYS.CHATGIS_SYSTEM_PROMPT);
  },

  /**
   * Set system prompt
   * @param {string} prompt - System prompt
   * @returns {boolean} - Success status
   */
  setSystemPrompt(prompt) {
    return storageOperations.setString(STORAGE_KEYS.CHATGIS_SYSTEM_PROMPT, prompt);
  },

  /**
   * Remove system prompt
   * @returns {boolean} - Success status
   */
  removeSystemPrompt() {
    return storageOperations.remove(STORAGE_KEYS.CHATGIS_SYSTEM_PROMPT);
  },

  /**
   * Get GeoNER enabled status
   * @returns {boolean|null} - GeoNER status or null if not found
   */
  getGeoNEREnabled() {
    return storageOperations.getJSON(STORAGE_KEYS.CHATGIS_GEO_NER_ENABLED);
  },

  /**
   * Set GeoNER enabled status
   * @param {boolean} enabled - GeoNER enabled status
   * @returns {boolean} - Success status
   */
  setGeoNEREnabled(enabled) {
    return storageOperations.setJSON(STORAGE_KEYS.CHATGIS_GEO_NER_ENABLED, enabled);
  },

  /**
   * Remove GeoNER enabled status
   * @returns {boolean} - Success status
   */
  removeGeoNEREnabled() {
    return storageOperations.remove(STORAGE_KEYS.CHATGIS_GEO_NER_ENABLED);
  },

  /**
   * Get map state
   * @returns {object|null} - Map state object or null if not found
   */
  getMapState() {
    return storageOperations.getJSON(STORAGE_KEYS.CHATGIS_MAP_STATE);
  },

  /**
   * Set map state
   * @param {object} mapState - Map state object
   * @returns {boolean} - Success status
   */
  setMapState(mapState) {
    return storageOperations.setJSON(STORAGE_KEYS.CHATGIS_MAP_STATE, mapState);
  },

  /**
   * Remove map state
   * @returns {boolean} - Success status
   */
  removeMapState() {
    return storageOperations.remove(STORAGE_KEYS.CHATGIS_MAP_STATE);
  },

  /**
   * Get About section expanded status
   * @returns {boolean|null} - About expanded status or null if not found
   */
  getAboutExpanded() {
    return storageOperations.getJSON(STORAGE_KEYS.CHATGIS_ABOUT_EXPANDED);
  },

  /**
   * Set About section expanded status
   * @param {boolean} expanded - About expanded status
   * @returns {boolean} - Success status
   */
  setAboutExpanded(expanded) {
    return storageOperations.setJSON(STORAGE_KEYS.CHATGIS_ABOUT_EXPANDED, expanded);
  },

  /**
   * Remove About section expanded status
   * @returns {boolean} - Success status
   */
  removeAboutExpanded() {
    return storageOperations.remove(STORAGE_KEYS.CHATGIS_ABOUT_EXPANDED);
  },

  /**
   * Get MCP tools
   * @returns {array|null} - MCP tools array or null if not found
   */
  getMCPTools() {
    return storageOperations.getJSON(STORAGE_KEYS.CHATGIS_MCP_TOOLS);
  },

  /**
   * Set MCP tools
   * @param {array} tools - MCP tools array
   * @returns {boolean} - Success status
   */
  setMCPTools(tools) {
    return storageOperations.setJSON(STORAGE_KEYS.CHATGIS_MCP_TOOLS, tools);
  },

  /**
   * Remove MCP tools
   * @returns {boolean} - Success status
   */
  removeMCPTools() {
    return storageOperations.remove(STORAGE_KEYS.CHATGIS_MCP_TOOLS);
  },

  /**
   * Clear all ChatGIS storage
   * @returns {boolean} - Success status
   */
  clearAll() {
    const results = [
      this.removeMessages(),
      this.removeSystemPrompt(),
      this.removeGeoNEREnabled(),
      this.removeMapState(),
      this.removeAboutExpanded(),
      this.removeMCPTools()
    ];
    return results.every(result => result);
  }
};

/**
 * RAG module storage methods
 */
export const ragStorage = {
  /**
   * Get chat messages
   * @returns {array|null} - Messages array or null if not found
   */
  getMessages() {
    return storageOperations.getJSON(STORAGE_KEYS.RAG_MESSAGES);
  },

  /**
   * Set chat messages
   * @param {array} messages - Messages array
   * @returns {boolean} - Success status
   */
  setMessages(messages) {
    return storageOperations.setJSON(STORAGE_KEYS.RAG_MESSAGES, messages);
  },

  /**
   * Remove chat messages
   * @returns {boolean} - Success status
   */
  removeMessages() {
    return storageOperations.remove(STORAGE_KEYS.RAG_MESSAGES);
  },

  /**
   * Get session ID
   * @returns {string|null} - Session ID or null if not found
   */
  getSessionId() {
    return storageOperations.getString(STORAGE_KEYS.RAG_SESSION_ID);
  },

  /**
   * Set session ID
   * @param {string} sessionId - Session ID
   * @returns {boolean} - Success status
   */
  setSessionId(sessionId) {
    return storageOperations.setString(STORAGE_KEYS.RAG_SESSION_ID, sessionId);
  },

  /**
   * Remove session ID
   * @returns {boolean} - Success status
   */
  removeSessionId() {
    return storageOperations.remove(STORAGE_KEYS.RAG_SESSION_ID);
  },

  /**
   * Get selected model
   * @returns {string|null} - Selected model or null if not found
   */
  getSelectedModel() {
    return storageOperations.getString(STORAGE_KEYS.RAG_SELECTED_MODEL);
  },

  /**
   * Set selected model
   * @param {string} model - Selected model
   * @returns {boolean} - Success status
   */
  setSelectedModel(model) {
    return storageOperations.setString(STORAGE_KEYS.RAG_SELECTED_MODEL, model);
  },

  /**
   * Remove selected model
   * @returns {boolean} - Success status
   */
  removeSelectedModel() {
    return storageOperations.remove(STORAGE_KEYS.RAG_SELECTED_MODEL);
  },

  /**
   * Get GeoNER enabled status
   * @returns {boolean|null} - GeoNER status or null if not found
   */
  getGeoNEREnabled() {
    return storageOperations.getJSON(STORAGE_KEYS.RAG_GEO_NER_ENABLED);
  },

  /**
   * Set GeoNER enabled status
   * @param {boolean} enabled - GeoNER enabled status
   * @returns {boolean} - Success status
   */
  setGeoNEREnabled(enabled) {
    return storageOperations.setJSON(STORAGE_KEYS.RAG_GEO_NER_ENABLED, enabled);
  },

  /**
   * Remove GeoNER enabled status
   * @returns {boolean} - Success status
   */
  removeGeoNEREnabled() {
    return storageOperations.remove(STORAGE_KEYS.RAG_GEO_NER_ENABLED);
  },

  /**
   * Get map state
   * @returns {object|null} - Map state object or null if not found
   */
  getMapState() {
    return storageOperations.getJSON(STORAGE_KEYS.RAG_MAP_STATE);
  },

  /**
   * Set map state
   * @param {object} mapState - Map state object
   * @returns {boolean} - Success status
   */
  setMapState(mapState) {
    return storageOperations.setJSON(STORAGE_KEYS.RAG_MAP_STATE, mapState);
  },

  /**
   * Remove map state
   * @returns {boolean} - Success status
   */
  removeMapState() {
    return storageOperations.remove(STORAGE_KEYS.RAG_MAP_STATE);
  },

  /**
   * Get About section expanded status
   * @returns {boolean|null} - About expanded status or null if not found
   */
  getAboutExpanded() {
    return storageOperations.getJSON(STORAGE_KEYS.RAG_ABOUT_EXPANDED);
  },

  /**
   * Set About section expanded status
   * @param {boolean} expanded - About expanded status
   * @returns {boolean} - Success status
   */
  setAboutExpanded(expanded) {
    return storageOperations.setJSON(STORAGE_KEYS.RAG_ABOUT_EXPANDED, expanded);
  },

  /**
   * Remove About section expanded status
   * @returns {boolean} - Success status
   */
  removeAboutExpanded() {
    return storageOperations.remove(STORAGE_KEYS.RAG_ABOUT_EXPANDED);
  },

  /**
   * Clear all RAG storage
   * @returns {boolean} - Success status
   */
  clearAll() {
    const results = [
      this.removeMessages(),
      this.removeSessionId(),
      this.removeSelectedModel(),
      this.removeGeoNEREnabled(),
      this.removeMapState(),
      this.removeAboutExpanded()
    ];
    return results.every(result => result);
  }
};

/**
 * Global storage operations
 */
export const globalStorage = {
  /**
   * Clear all application storage
   * @returns {boolean} - Success status
   */
  clearAll() {
    const results = [
      authStorage.removeToken(),
      settingsStorage.removeUserSettings(),
      chatGISStorage.clearAll(),
      ragStorage.clearAll()
    ];
    return results.every(result => result);
  }
};

/**
 * Export storage keys for reference (if needed)
 */
export { STORAGE_KEYS };
